import { useEffect } from 'react';

/**
 * App-icon badge (W6, Personal tier): the Badging API —
 * `navigator.setAppBadge(n)` — shows "what's left of today" on the installed
 * app's icon (iOS 16.4+ Home Screen web apps, Chrome / Edge installed apps).
 * Everywhere else it's a silent no-op.
 *
 * The service worker sets the same number from a plan push's `badge`
 * (`plan-push.ts`), so the icon is right even while the app is closed; this
 * hook keeps it right while the app is open.
 */

interface BadgeHost {
  setAppBadge?: (contents?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
}

export function isAppBadgeSupported(host: BadgeHost | undefined = globalNavigator()): boolean {
  return !!host && typeof host.setAppBadge === 'function';
}

function globalNavigator(): BadgeHost | undefined {
  return typeof navigator === 'undefined' ? undefined : (navigator as unknown as BadgeHost);
}

/** Set the badge to `count` (0 / negative clears it). Never throws; false when unsupported or refused. */
export async function applyAppBadge(
  count: number,
  host: BadgeHost | undefined = globalNavigator(),
): Promise<boolean> {
  if (!host || !isAppBadgeSupported(host)) return false;
  try {
    if (count > 0) await host.setAppBadge!(Math.floor(count));
    else if (typeof host.clearAppBadge === 'function') await host.clearAppBadge();
    else await host.setAppBadge!(0);
    return true;
  } catch {
    // NotAllowedError (no notification permission on iOS) / not installed.
    return false;
  }
}

/**
 * Keep the app badge equal to `count` while mounted. `null` = unknown yet —
 * leave the badge alone (don't flash it away while counts load).
 */
export function useAppBadge(count: number | null): void {
  useEffect(() => {
    if (count === null) return;
    void applyAppBadge(count);
  }, [count]);
}
